import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// Ligações do número na Meta (POST /{phone-number-id}/settings). Não há nada no
// banco: a configuração vive na Meta, por isso só aparece em conexão já salva e
// tem botão próprio, separado do "Salvar" da conexão.

const normalize = (calling) => ({
  status: calling?.status === "ENABLED" ? "ENABLED" : "DISABLED",
  call_icon_visibility: calling?.call_icon_visibility === "DISABLE_ALL" ? "DISABLE_ALL" : "DEFAULT",
  callback_permission_status: calling?.callback_permission_status === "ENABLED" ? "ENABLED" : "DISABLED",
});

const CallingSettingsSection = ({ whatsAppId }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const { data } = await api.get(`/calls/whatsapp/${whatsAppId}/settings`);
        if (!cancelled) setSettings(normalize(data?.calling));
      } catch (err) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [whatsAppId]);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/calls/whatsapp/${whatsAppId}/settings`, settings);
      setSettings(normalize(data?.calling));
      toast.success(i18n.t("calls.settings.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box mt={2}>
      <Typography variant="subtitle1" gutterBottom>
        {i18n.t("calls.settings.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {i18n.t("calls.settings.help")}
      </Typography>

      {loading && <CircularProgress size={20} />}

      {!loading && loadError && (
        <Typography variant="body2" color="error">
          {i18n.t("calls.settings.loadError")}
        </Typography>
      )}

      {!loading && settings && (
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>{i18n.t("calls.settings.status")}</InputLabel>
              <Select value={settings.status} onChange={handleChange("status")}>
                <MenuItem value="ENABLED">{i18n.t("calls.settings.enabled")}</MenuItem>
                <MenuItem value="DISABLED">{i18n.t("calls.settings.disabled")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>{i18n.t("calls.settings.iconVisibility")}</InputLabel>
              <Select value={settings.call_icon_visibility} onChange={handleChange("call_icon_visibility")}>
                <MenuItem value="DEFAULT">{i18n.t("calls.settings.iconDefault")}</MenuItem>
                <MenuItem value="DISABLE_ALL">{i18n.t("calls.settings.iconHidden")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>{i18n.t("calls.settings.callbackPermission")}</InputLabel>
              <Select
                value={settings.callback_permission_status}
                onChange={handleChange("callback_permission_status")}
              >
                <MenuItem value="ENABLED">{i18n.t("calls.settings.enabled")}</MenuItem>
                <MenuItem value="DISABLED">{i18n.t("calls.settings.disabled")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button type="button" variant="outlined" color="primary" disabled={saving} onClick={handleSave}>
              {saving && <CircularProgress size={18} style={{ marginRight: 8 }} />}
              {i18n.t("calls.settings.save")}
            </Button>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default CallingSettingsSection;
